import { wolfswapCookieApi } from '@/lib/api/clients/wolfswap';

import {
    callbackResponseSchema,
    loginResponseSchema,
    logoutResponseSchema,
    meResponseSchema,
    rebindResponseSchema,
} from './schemas';
import type {
    CallbackResponse,
    LoginResponse,
    LogoutResponse,
    MeResponse,
    OAuthProvider,
    RebindResponse,
} from './types';

/**
 * Service functions for the MoonHub.API v2-auth (social login) flow.
 *
 * All calls use {@link wolfswapCookieApi} (no JWT, but `credentials: 'include'`)
 * because the flow is driven entirely by the HttpOnly session cookie the backend
 * sets. The accountHash this yields is what the speed-trading deposit needs to
 * auto-provision the user's HyperEVM custodial wallet server-side.
 */

/** Begin login: returns the provider authorize URL to redirect the browser to. */
export async function startOAuthLogin(
    provider: OAuthProvider,
): Promise<LoginResponse> {
    const data = await wolfswapCookieApi
        .post('v2/auth/login', { json: { provider } })
        .json();
    return loginResponseSchema.parse(data);
}

/** Complete login after the provider redirects back with `code` + `state`. */
export async function completeOAuthCallback(
    code: string,
    state: string,
): Promise<CallbackResponse> {
    const data = await wolfswapCookieApi
        .post('v2/auth/callback', { json: { code, state } })
        .json();
    return callbackResponseSchema.parse(data);
}

/** Read the current session (authenticated:false when logged out). */
export async function fetchOAuthMe(): Promise<MeResponse> {
    const data = await wolfswapCookieApi.get('v2/auth/me').json();
    return meResponseSchema.parse(data);
}

/** Re-check the on-chain custodial wallet binding for the active session. */
export async function rebindOAuth(): Promise<RebindResponse> {
    const data = await wolfswapCookieApi
        .post('v2/auth/rebind', { json: {} })
        .json();
    return rebindResponseSchema.parse(data);
}

/** Clear the session (server clears cookies). */
export async function logoutOAuth(): Promise<LogoutResponse> {
    const data = await wolfswapCookieApi.post('v2/auth/logout').json();
    return logoutResponseSchema.parse(data);
}
